import { Injectable } from "@angular/core";
import * as quillToWord from 'quill-to-word';
import { quillToWordConfig } from "../utils/quil-to-word.util";
import html2canvas, { Options } from "html2canvas";
import { saveAs } from "file-saver";
import { AlignmentType, BorderStyle, HeadingLevel, ImageRun, PageBreak, Paragraph, PatchType, ShadingType, Table, TableCell, TableRow, TextRun, patchDocument } from "docx";
import { CONTRAST_COLOR, convertScoreToPlusMinusScore, itemCategoryToHexColor, translateItemCategory } from "../utils/item.util";

import Quill, { Delta as DeltaType } from "quill";
import { Item } from "../models/item.model";
import { TransparentType } from "../models/transparent-type";
import { ImageConf } from "../models/image-conf.model";
import { Hp } from "../models/hp.model";
import { Stakeholder } from "../models/stakeholder.model";
import { formatDate } from "@angular/common";
const Delta = Quill.import('delta') as typeof DeltaType

@Injectable({
  providedIn: 'root'
})
export class WordExportService {
  private static CURRENT_RESULT_TABLE_TITLE = 'Resultaten - Beeldvorming huidige situatie';
  private static FUTURE_RESULT_TABLE_TITLE = 'Resultaten - Beeldvorming toekomstige situatie';

  private static BEGIN_OF_PAGE_PATCH = 'BEGIN_OF_PAGE_PATCH';
  private static END_OF_PAGE_PATCH = 'END_OF_PAGE_PATCH';
  private static PAGE_BREAK_PATCH = 'PAGE_BREAK';
  private static PREFIX_SUMMARY_PATCH = 'SUMMARY_';
  private static PREFIX_IMAGE_PATCH = 'IMAGE_';

  private static TITLE_TEXT_SIZE = 45;
  private static SUMMARY_TEXT_SIZE = 24;

  private static GRAPH_EXPLAIN = `Door de huidige en toekomstige beoordeling te visualiseren in een spiderweb, wordt zichtbaar op welke thema's er opgaven zijn richting de toekomst. Onderstaande spiderweb toont de weergave van de beoordeling op basis van consensus.`;

  private images = new Map<string, ImageConf[]>();

  public async generateWordFromItems(hp: Hp, items: Item[], stakeholders: Stakeholder[]) {
    const currentItems = items.filter(item => item.isCurrentSituation);
    const futureItems = items.filter(item => !item.isCurrentSituation);
    const combinedDelta = this.getCombinedItemsDelta(items);
    const docAsBlob = await quillToWord.generateWord(combinedDelta as any, quillToWordConfig) as Blob;
    const spiderImg = await this.getSpiderGraphImg();
    
    const patch = await patchDocument({
      outputType: "blob",
      data: docAsBlob, 
      patches: {
        [WordExportService.BEGIN_OF_PAGE_PATCH]: {
          type: PatchType.DOCUMENT,
          children: [...this.getFrontPagePatchChildren(hp, stakeholders)]
        },

        //Title above each item (outside quill Delta)
        ...this.getTitlePatches(items),

        //Summary below each item (outside quill Delta)
        ...this.getSummaryPatch(items),

        //Images extracted from Delta and inserted for own conversion
        ...this.getImagePatches(items),

        [WordExportService.END_OF_PAGE_PATCH]: {
          type: PatchType.DOCUMENT,
          children: [
            //Graph patch children
            ...this.getGraphPatchChildren(spiderImg),

            //Result table patch children
            this.getTableTitlePatchChildren(true),
            this.getResultTableChildren(currentItems),
            new Paragraph({
              children: [new PageBreak()]
            }),
            this.getTableTitlePatchChildren(false),
            this.getResultTableChildren(futureItems)
          ]
        },
        [WordExportService.PAGE_BREAK_PATCH]: {
          type: PatchType.PARAGRAPH,
          children: [
            new PageBreak()
          ]
        }
      }
    });

    saveAs(new Blob([patch]), `Handelingsperspectief ${hp.location}.docx`);
  }

  private getCombinedItemsDelta(items: Item[]) {
    const doubleLineBreak = new Delta({ ops: [{ insert: `\n` }, { insert: `\n` }] });
    const pageBreakDelta = new Delta({ ops: [{ insert: `{{${WordExportService.PAGE_BREAK_PATCH}}}` }] });

    let combinedDelta = new Delta({ ops: [{ insert: `\n{{${WordExportService.END_OF_PAGE_PATCH}}}` }] });
    items.slice().reverse().forEach((item, i) => {
      const titlePatch = new Delta({ ops: [{ insert: `{{${item.itemId}}}\n` }] });
      const summaryPatch = new Delta({ ops: [{ insert: `\n\n{{${WordExportService.PREFIX_SUMMARY_PATCH}${item.itemId}}}\n` }] });

      this.extractImagesFromItem(item);
      const currentItemContentDelta = new Delta(JSON.parse(item.content!));

      combinedDelta = combinedDelta.compose(pageBreakDelta);
      combinedDelta = combinedDelta.compose(summaryPatch);
      combinedDelta = combinedDelta.compose(currentItemContentDelta);
      combinedDelta = combinedDelta.compose(titlePatch);

      if (i !== items.length - 1) {
        combinedDelta = combinedDelta.compose(doubleLineBreak);
      }
    });

    const beginOfDocumentPatch = new Delta({ ops: [{ insert: `{{${WordExportService.BEGIN_OF_PAGE_PATCH}}}` }] });
    combinedDelta = combinedDelta.compose(new Delta({ ops: [{ insert: `\n{{${WordExportService.PAGE_BREAK_PATCH}}}\n` }] }));
    combinedDelta = combinedDelta.compose(beginOfDocumentPatch);

    return combinedDelta;
  }

  private async getSpiderGraphImg() {
    const spiderElement: Element[] = Array.from(document.querySelectorAll('.graph-container'));
    const canvasOptions: Partial<Options> = {
      scale: 2
    }

    if (spiderElement.length !== 1) {
      console.error(`${spiderElement.length} elements with class graph-container found. Only one is expected. Graph container will not show in Word document.`);
    }

    try {
      const spiderCanvas = await html2canvas(spiderElement[0] as HTMLElement, canvasOptions);
      return spiderCanvas.toDataURL('image/png');
    } catch {
      console.error('element with class graph-container could not be found. Graph container will not show in Word document.')
      return '';
    }
  }

  private getTitlePatches(items: Item[]) {
    const patchMap = items.map(item => {
      return {
        [item.itemId.toString()]: {
          type: PatchType.DOCUMENT,
          children: [
            new Paragraph({
              children: [
                new TextRun({
                  text: translateItemCategory(item.type.category),
                  size: WordExportService.TITLE_TEXT_SIZE,
                  color: itemCategoryToHexColor(item.type.category).slice(1)
                })
              ]
            }),
            new Paragraph({
              children: [
                new TextRun({
                  text: this.getFullItemTitle(item),
                  size: WordExportService.TITLE_TEXT_SIZE,
                  color: itemCategoryToHexColor(item.type.category).slice(1)
                })
              ]
            })
          ]
        }
      }
    });

    return Object.assign({}, ...patchMap.map((patch) => ({ [Object.keys(patch)[0]]: patch[Object.keys(patch)[0]] })));
  }

  private getSummaryPatch(items: Item[]) {
    const patchMap = items.map(item => {
      const patchKey = `${WordExportService.PREFIX_SUMMARY_PATCH}${item.itemId}`;
      return {
        [patchKey]: {
          type: PatchType.DOCUMENT,
          children: [
            new Paragraph({
              children: [
                new TextRun({
                  text: 'Samenvattend statement',
                  bold: true,
                  size: WordExportService.SUMMARY_TEXT_SIZE,
                  color: itemCategoryToHexColor(item.type.category).slice(1)
                })
              ]
            }),
            new Paragraph({
              children: [new TextRun({ text: item.summary ?? 'Geen samenvatting opgesteld.' })]
            })
          ]
        }
      }
    });

    return Object.assign({}, ...patchMap.map((patch) => ({ [Object.keys(patch)[0]]: patch[Object.keys(patch)[0]] })));
  }

  private getImagePatches(items: Item[]) {
    const patchMap = items.map(item => {
      const patchKey = `${WordExportService.PREFIX_IMAGE_PATCH}${item.itemId}`;
      return {
        [patchKey]: {
          type: PatchType.PARAGRAPH,
          children: this.getImagePatchChildren(patchKey)
        }
      }
    });

    return Object.assign({}, ...patchMap.map((patch) => ({ [Object.keys(patch)[0]]: patch[Object.keys(patch)[0]] })));
  }

  private getImagePatchChildren(patchKey: string): ImageRun[] {
    const images = this.images.get(patchKey);
    if (images) {
      return images.map(imageConf => {
        return new ImageRun({
          type: 'png',
          data: imageConf.image,
          transformation: {
            width: imageConf.width,
            height: imageConf.height
          }
        });
      });
    } else {
      return [];
    }
  }

  private getGraphPatchChildren(graphImage: string) {
    return [
      new Paragraph({
        children: [
          new TextRun({
            text: 'Spiderweb grafiek',
            size: WordExportService.TITLE_TEXT_SIZE,
            color: CONTRAST_COLOR.slice(1),
            bold: true
          })
        ]
      }),
      new Paragraph({
        children: [
          new TextRun({
            text: WordExportService.GRAPH_EXPLAIN
          })
        ]
      }),
      new Paragraph({
        children: [
          new ImageRun({
            type: 'png',
            data: graphImage,
            transformation: {
              width: 700,
              height: 700
            }
          }),
          new PageBreak()
        ]
      })
    ];
  }

  private getFrontPagePatchChildren(hp: Hp, contributers: Stakeholder[]) {
    const supporters = contributers.filter(contributers => contributers.supporting);
    const stakeholders = contributers.filter(contributer => !contributer.supporting);
    const frontPageChildren: Paragraph[] = [
      new Paragraph({
        text: `Handelingsperspectief ${hp.location}`,
        heading: HeadingLevel.HEADING_1,
        alignment: AlignmentType.CENTER
      }),
    ];

    if (stakeholders.length > 0) {
      const madeBy = stakeholders.reduce((prev, current) => { return `${prev}${current.name}, ` }, '').slice(0, -2);
      frontPageChildren.push(this.getSingleCenteredTextRunParagraph(`Stakeholders: ${madeBy}`, 30));
    }

    if (supporters.length > 0) {
      const madeBy = supporters.reduce((prev, current) => { return `${prev}${current.organisation}, ` }, '').slice(0, -2);
      frontPageChildren.push(this.getSingleCenteredTextRunParagraph(`Ondersteunende partijen: ${madeBy}`, 30));
    }

    frontPageChildren.push(
      this.getSingleCenteredTextRunParagraph(
        `Datum: ${formatDate(Date.now(), 'd-MM-yy', 'nl_NL')}`,
        20
      )
    );

    return frontPageChildren;
  }

  private getSingleCenteredTextRunParagraph(text: string, size: number) {
    return new Paragraph({
      children: [
        new TextRun({
          text,
          size,
          color: CONTRAST_COLOR.slice(1)
        })
      ],
      alignment: AlignmentType.CENTER
    })
  }

  private getTableTitlePatchChildren(isCurrentSituation: boolean) {
    return new Paragraph({
      children: [
        new TextRun({
          text: isCurrentSituation ? WordExportService.CURRENT_RESULT_TABLE_TITLE : WordExportService.FUTURE_RESULT_TABLE_TITLE,
          size: WordExportService.TITLE_TEXT_SIZE,
          color: CONTRAST_COLOR.slice(1),
          bold: true
        })
      ]
    });
  }

  private getResultTableChildren(items: Item[]) {
    const tableRows: TableRow[] = items.map((item, i) => {
      return new TableRow({
        children: [
          new TableCell({
            children: [new Paragraph(item.type.name)],
            borders: {
              left: { style: BorderStyle.NONE, size: 1 },
              top: { style: BorderStyle.NONE, size: 1 },
              bottom: { style: BorderStyle.NONE, size: 1 }
            },
            margins: { left: 100 },
            shading: this.getResultTableCellColor(item, i + 1)
          }),
          new TableCell({
            children: [new Paragraph(convertScoreToPlusMinusScore(item.consensusScore, true))],
            borders: {
              end: { style: BorderStyle.NONE, size: 1 },
              top: { style: BorderStyle.NONE, size: 1 },
              bottom: { style: BorderStyle.NONE, size: 1 }
            },
            margins: { left: 100 },
            shading: {
              type: ShadingType.SOLID,
              color: i % 2 ? '#7ac6c6' : '3dafaf'
            }
          }),
          new TableCell({
            children: [new Paragraph(item.underpinning ?? 'Geen conclusie ingevuld')],
            borders: {
              start: { style: BorderStyle.NONE, size: 1 },
              end: { style: BorderStyle.NONE, size: 1 },
              top: { style: BorderStyle.NONE, size: 1 },
              bottom: { style: BorderStyle.NONE, size: 1 }
            },
            margins: { left: 100 },
            shading: {
              type: ShadingType.SOLID,
              color: i % 2 ? '#7ac6c6' : '3dafaf'
            }
          })
        ],
        cantSplit: true,
        tableHeader: true,
      });
    });

    return new Table({
      rows: [this.getResultTableHeader(), ...tableRows]
    });
  }

  private getResultTableHeader() {
    return new TableRow({
      children: [
        new TableCell({
          children: [this.getResultTableHeaderText('Items')],
          borders: {
            top: { style: BorderStyle.NONE, size: 1 },
            start: { style: BorderStyle.NONE, size: 1 }
          }
        }),
        new TableCell({
          children: [this.getResultTableHeaderText('Consensus')],
          borders: {
            top: { style: BorderStyle.NONE, size: 1 },
            end: { style: BorderStyle.NONE, size: 1 }
          }
        }),
        new TableCell({
          children: [this.getResultTableHeaderText('Conclusie')],
          borders: {
            top: { style: BorderStyle.NONE, size: 1 },
            end: { style: BorderStyle.NONE, size: 1 },
            start: { style: BorderStyle.NONE, size: 1 }
          }
        }),
      ]
    });
  }

  private getResultTableHeaderText(text: string) {
    return new Paragraph({
      children: [
        new TextRun({
          text: text,
          bold: true
        })
      ],
      alignment: AlignmentType.CENTER
    });
  }

  private getResultTableCellColor(item: Item, i: number) {
    const transparencyType: TransparentType = i % 2 ? TransparentType.DARK : TransparentType.LIGHT;

    return {
      type: ShadingType.SOLID,
      color: itemCategoryToHexColor(item.type.category, transparencyType)
    }
  }

  private extractImagesFromItem(item: Item) {
    const content = JSON.parse(item.content!);

    content.ops = content.ops.map((deltaObject: any) => {
      if (typeof deltaObject.insert !== 'string') {
        const savedImageDelta = this.saveImage(item, deltaObject);
        return savedImageDelta;
      } else {
        return deltaObject;
      }
    });

    item.content = JSON.stringify(content);
  }

  private saveImage(item: Item, deltaObject: any) {
    const imagePatch = `${WordExportService.PREFIX_IMAGE_PATCH}${item.itemId}`;
    const imageConf: ImageConf = this.getImageConf(deltaObject);

    if (this.images.has(imagePatch)) {
      this.images.set(imagePatch, [imageConf, ...this.images.get(imagePatch)!]);
      return { insert: '' };
    } else {
      this.images.set(imagePatch, [imageConf]);
      return { insert: `\n{{${imagePatch}}}\n` };
    }
  }

  private getImageConf(deltaObject: any): ImageConf {
    if (deltaObject.attributes) {
      return {
        image: deltaObject.insert.image,
        width: deltaObject.attributes.width,
        height: deltaObject.attributes.height
      }
    } else {
      const image = this.findImageElementBySrc(deltaObject.insert.image);
      return {
        image: deltaObject.insert.image,
        width: image ? image.naturalWidth : 100,
        height: image ? image.naturalHeight : 100
      }
    }
  }

  private findImageElementBySrc(src: string): HTMLImageElement | null {
    const imgElements = document.getElementsByTagName('img');

    for (let i = 0; i < imgElements.length; i++) {
      if (imgElements[i].src === src) {
        return imgElements[i];
      }
    }

    // If no image with the specified src is found, return null
    return null;
  }

  private getFullItemTitle(item: Item) {
    return `${item.type.name} - ${item.isCurrentSituation ? 'huidige situatie' : 'toekomstige situatie'}`
  }

}
